import { useEffect, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui';
import { formatCurrency, minorToMajor } from '@/lib';
import {
  airbnbNoteFor,
  isAirbnbHostname,
  type AirbnbFlow,
} from '@/lib/airbnbInstructions';

function isFlow(value: string | null): value is AirbnbFlow {
  return value === 'resolution_center' || value === 'reservation_alteration';
}

type InstructionSet = {
  title: string;
  intro: string;
  steps: string[];
  ctaLabel: string | null;
  manualFallback: string | null;
};

// Strings live inline in t() calls so i18next-parser can extract them statically.
function buildInstructions(flow: AirbnbFlow, t: TFunction, notice24h: boolean): InstructionSet {
  if (flow === 'resolution_center') {
    return {
      title: t('Pay your host through Airbnb'),
      intro: t("Here's what you'll do on Airbnb:"),
      steps: [
        t('Select "Send money".'),
        t('Under "What is this payment for?", choose "Extra services".'),
        t('Enter the exact amount shown above, then paste the note into the "Note" field.'),
        t('Tap "Next", then choose your payment method and tap "Confirm and pay".'),
      ],
      ctaLabel: t('Open Airbnb'),
      manualFallback: t(
        'Button not working? Open the Airbnb app or airbnb.com, go to Trips → this reservation → Get help → Resolution Center.'
      ),
    };
  }
  return {
    title: t('Confirm your stay extension on Airbnb'),
    intro: t(
      'Stay extensions on Airbnb are handled through a reservation alteration request. Your host will send the alteration to you in Airbnb. Accept it there and Airbnb will charge your payment method on file.'
    ),
    steps: [
      notice24h
        ? t('Please allow us 24 hours to send the alteration request.')
        : t('Your host will send you a reservation alteration request via Airbnb shortly.'),
      t('Open the Airbnb message thread for this reservation, or check Trips → your reservation.'),
      t('Review the new dates and updated total on the alteration request.'),
      t('Tap "Accept". Airbnb will charge your payment method on file automatically.'),
    ],
    ctaLabel: null,
    manualFallback: null,
  };
}

function AirbnbInstructions() {
  const { t } = useTranslation('upsells');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const notice24h = searchParams.get('notice') === '24h';

  const inPortal = location.pathname.startsWith('/portal/');
  const addOnsPath = inPortal ? '/portal/home/add-ons' : '/add-ons';

  const flowParam = searchParams.get('flow');
  const flow: AirbnbFlow = isFlow(flowParam) ? flowParam : 'resolution_center';
  const title = searchParams.get('title') ?? '';
  const optionName = searchParams.get('optionName');
  const priceRaw = searchParams.get('price');
  const currency = searchParams.get('currency') ?? 'USD';
  const rawAirbnbUrl = searchParams.get('airbnbUrl') ?? '';
  // Defend against open-redirect: only honor URLs on airbnb.com.
  const airbnbUrl = isAirbnbHostname(rawAirbnbUrl) ? rawAirbnbUrl : '';

  const instructionSet = useMemo(() => buildInstructions(flow, t, notice24h), [flow, t, notice24h]);
  const priceMinor = priceRaw ? Number(priceRaw) : null;
  const priceDisplay = useMemo(() => {
    if (priceMinor == null || Number.isNaN(priceMinor)) return null;
    return formatCurrency(minorToMajor(priceMinor, currency), currency);
  }, [priceMinor, currency]);

  const noteText = useMemo(() => airbnbNoteFor(title, optionName), [title, optionName]);

  useEffect(() => {
    document.title = t('Airbnb Instructions');
  }, [t]);

  const handleCopyNote = async () => {
    try {
      await navigator.clipboard.writeText(noteText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleOpenAirbnb = () => {
    if (!airbnbUrl) return;
    window.open(airbnbUrl, '_blank', 'noopener,noreferrer');
  };

  const isResolutionCenter = flow === 'resolution_center';

  return (
    <div className="pb-8">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-zinc-200">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate(addOnsPath)}
            className="w-10 h-10 rounded-full hover:bg-zinc-100 flex items-center justify-center"
            aria-label={t('Back')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-zinc-900 truncate">{instructionSet.title}</h1>
        </div>
      </div>

      <div className="px-4 pt-5 space-y-6">
        {/* Summary card */}
        {(title || priceDisplay) && (
          <div
            className="bg-white rounded-2xl border border-zinc-200 p-5"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)' }}
          >
            {title && <h2 className="text-xl font-bold text-zinc-900">{title}</h2>}
            {optionName && <p className="mt-1 text-sm text-zinc-500">{optionName}</p>}
            {priceDisplay && (
              <p className="mt-3 text-2xl font-semibold text-zinc-900 tabular-nums">{priceDisplay}</p>
            )}

            {isResolutionCenter && (
              <div className="mt-5 rounded-xl bg-zinc-50 border border-zinc-200 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  {t('Paste this in the Note field')}
                </p>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="text-base font-semibold text-zinc-900 break-words">{noteText}</span>
                  <button
                    type="button"
                    onClick={handleCopyNote}
                    className="shrink-0 px-3 py-1.5 text-sm font-medium rounded-full border border-zinc-300 hover:bg-zinc-100"
                  >
                    {copied ? t('Copied') : t('Copy')}
                  </button>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Intro */}
        <p className="text-[15px] leading-relaxed text-zinc-600">{instructionSet.intro}</p>

        {/* Steps */}
        <ol className="space-y-6">
          {instructionSet.steps.map((step, i) => (
            <li
              key={i}
              className="bg-white rounded-2xl border border-zinc-200 overflow-hidden"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
            >
              <div className="flex items-start gap-3 p-5">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[var(--besty-blue,#3b82f6)] text-white text-sm font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <p className="text-[15px] leading-relaxed text-zinc-800">{step}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* CTA below steps — forces guest to scroll past the steps before tapping */}
        {airbnbUrl && instructionSet.ctaLabel && (
          <div className="pt-2">
            <Button
              onClick={handleOpenAirbnb}
              className="w-full rounded-full h-14 text-base font-medium"
              variant="primary"
              size="lg"
            >
              {instructionSet.ctaLabel}
            </Button>
            {instructionSet.manualFallback && (
              <p className="mt-2 text-xs text-zinc-500 text-center leading-snug">
                {instructionSet.manualFallback}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AirbnbInstructions;
